import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { alertService, analyticsService, deviceService, getWebSocketUrl } from '../services/api.ts';

// Types for Administrative Dashboard
interface DeviceMetrics {
  device_id: string;
  cpu_usage: number;
  memory_usage: number;
  gpu_usage: number;
  gpu_memory_usage: number;
  cpu_temperature: number;
  gpu_temperature: number;
  inference_fps: number;
  network_latency: number;
  timestamp: number;
}

interface JetsonDevice {
  device_id: string;
  name: string;
  location_id: number | null;
  status: 'online' | 'offline' | 'maintenance' | 'error' | 'active' | 'inactive';
  last_seen: string;
  model: string;
  serial_number: string;
  jetpack_version: string;
  cuda_version: string;
  tensorrt_version: string;
  current_metrics: DeviceMetrics | null;
}

interface SystemAlert {
  id: number;
  device_id: string;
  type: 'performance' | 'temperature' | 'network' | 'storage' | 'system';
  severity: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  timestamp: number;
  acknowledged: boolean;
}

interface AdminState {
  isConnected: boolean;
  devices: JetsonDevice[];
  selectedDevice: JetsonDevice | null;
  systemAlerts: SystemAlert[];
  systemStats: {
    total_devices: number;
    online_devices: number;
    offline_devices: number;
    total_locations: number;
    avg_cpu_usage: number;
    avg_gpu_usage: number;
    avg_temperature: number;
  } | null;
  error: string | null;
}

type AdminAction =
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_DEVICES'; payload: JetsonDevice[] }
  | { type: 'UPDATE_DEVICE'; payload: JetsonDevice }
  | { type: 'UPDATE_DEVICE_METRICS'; payload: { device_id: string; metrics: DeviceMetrics } }
  | { type: 'SET_SELECTED_DEVICE'; payload: JetsonDevice | null }
  | { type: 'SET_SYSTEM_ALERTS'; payload: SystemAlert[] }
  | { type: 'ADD_ALERT'; payload: SystemAlert }
  | { type: 'ACKNOWLEDGE_ALERT'; payload: number }
  | { type: 'SET_SYSTEM_STATS'; payload: any }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

const initialState: AdminState = {
  isConnected: false,
  devices: [],
  selectedDevice: null,
  systemAlerts: [],
  systemStats: null,
  error: null,
};

function adminReducer(state: AdminState, action: AdminAction): AdminState {
  switch (action.type) {
    case 'SET_CONNECTED':
      return { ...state, isConnected: action.payload };
    case 'SET_DEVICES':
      return { ...state, devices: action.payload };
    case 'UPDATE_DEVICE':
      return {
        ...state,
        devices: state.devices.map(device => 
          device.device_id === action.payload.device_id ? action.payload : device
        ),
        selectedDevice: state.selectedDevice?.device_id === action.payload.device_id 
          ? action.payload : state.selectedDevice
      };
    case 'UPDATE_DEVICE_METRICS': {
      const updateMetrics = (device: JetsonDevice): JetsonDevice =>
        device.device_id === action.payload.device_id
          ? { ...device, current_metrics: action.payload.metrics }
          : device;
      return {
        ...state,
        devices: state.devices.map(updateMetrics),
        selectedDevice: state.selectedDevice ? updateMetrics(state.selectedDevice) : null,
      };
    }
    case 'SET_SELECTED_DEVICE':
      return { ...state, selectedDevice: action.payload };
    case 'SET_SYSTEM_ALERTS':
      return { ...state, systemAlerts: action.payload };
    case 'ADD_ALERT':
      return { 
        ...state, 
        systemAlerts: [action.payload, ...state.systemAlerts].slice(0, 100) 
      };
    case 'ACKNOWLEDGE_ALERT':
      return {
        ...state,
        systemAlerts: state.systemAlerts.map(alert =>
          alert.id === action.payload ? { ...alert, acknowledged: true } : alert
        )
      };
    case 'SET_SYSTEM_STATS':
      return { ...state, systemStats: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

interface AdminContextType {
  state: AdminState;
  selectDevice: (device: JetsonDevice | null) => void;
  acknowledgeAlert: (alertId: number) => Promise<void>;
  refreshDevices: () => Promise<void>;
  refreshSystemStats: () => Promise<void>;
  clearError: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

interface AdminProviderProps {
  children: ReactNode;
}

export function AdminProvider({ children }: AdminProviderProps) {
  const [state, dispatch] = useReducer(adminReducer, initialState);

  // Initialize WebSocket connection
  useEffect(() => {
    const wsUrl = getWebSocketUrl();
    let socket: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let shouldReconnect = true;

    const handleMessage = (event: MessageEvent<string>) => {
      try {
        const message = JSON.parse(event.data);
        const { type, data } = message;

        switch (type) {
          case 'connection_status':
            dispatch({ type: 'SET_CONNECTED', payload: Boolean(data?.connected) });
            break;
          case 'device_update':
            dispatch({ type: 'UPDATE_DEVICE', payload: data });
            break;
          case 'device_metrics':
            dispatch({ type: 'UPDATE_DEVICE_METRICS', payload: data });
            break;
          case 'system_alert':
            dispatch({ type: 'ADD_ALERT', payload: data });

            if (data.severity === 'critical') {
              toast.error(`Alertă critică: ${data.title}`, {
                duration: 8000,
              });
            } else if (data.severity === 'error') {
              toast.error(`Eroare: ${data.title}`);
            } else if (data.severity === 'warning') {
              toast(`Atenție: ${data.title}`, {
                icon: '⚠️',
                style: {
                  background: '#fbbf24',
                  color: '#1e293b',
                },
              });
            }
            break;
          case 'system_stats':
            dispatch({ type: 'SET_SYSTEM_STATS', payload: data });
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    const connect = () => {
      socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('Connected to PostureXe Admin backend');
        dispatch({ type: 'SET_CONNECTED', payload: true });
        dispatch({ type: 'CLEAR_ERROR' });

        // Request initial data
        refreshDevices();
        refreshSystemStats();
      };

      socket.onmessage = handleMessage;

      socket.onclose = () => {
        console.log('Disconnected from PostureXe Admin backend');
        dispatch({ type: 'SET_CONNECTED', payload: false });

        if (shouldReconnect) {
          const reconnectDelay = Number(process.env.REACT_APP_WEBSOCKET_RECONNECT_INTERVAL || 5000);
          reconnectTimeout = setTimeout(connect, reconnectDelay);
        }
      };

      socket.onerror = () => {
        const errorMessage = 'WebSocket connection error';
        console.error(errorMessage);
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
      };
    };

    connect();

    return () => {
      shouldReconnect = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      socket?.close();
    };
  }, []);

  // API functions
  const selectDevice = (device: JetsonDevice | null): void => {
    dispatch({ type: 'SET_SELECTED_DEVICE', payload: device });
  };

  const acknowledgeAlert = async (alertId: number): Promise<void> => {
    try {
      await alertService.acknowledgeAlert(alertId);

      dispatch({ type: 'ACKNOWLEDGE_ALERT', payload: alertId });
      toast.success('Alerta a fost confirmată');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(`Eroare la confirmarea alertei: ${errorMessage}`);
      throw error;
    }
  };

  const refreshDevices = async (): Promise<void> => {
    try {
      const devices = await deviceService.getDevices(undefined, undefined, true);
      dispatch({ type: 'SET_DEVICES', payload: devices });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      console.error('Failed to refresh devices:', errorMessage);
    }
  };

  const refreshSystemStats = async (): Promise<void> => {
    try {
      const overview = await analyticsService.getSystemOverview();
      const mapped = {
        total_devices: overview.devices?.total ?? 0,
        online_devices: overview.devices?.online ?? 0,
        offline_devices: overview.devices?.offline ?? 0,
        total_locations: overview.locations?.total ?? 0,
        avg_cpu_usage: 0,
        avg_gpu_usage: 0,
        avg_temperature: 0,
      };
      dispatch({ type: 'SET_SYSTEM_STATS', payload: mapped });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Failed to refresh system stats:', errorMessage);
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Periodic status updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (state.isConnected) {
        refreshDevices();
        refreshSystemStats();
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [state.isConnected]);

  const contextValue: AdminContextType = {
    state,
    selectDevice,
    acknowledgeAlert,
    refreshDevices,
    refreshSystemStats,
    clearError,
  };

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextType {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

